import { useState } from "react";
import { Plus, X } from "lucide-react";
import { Button } from "@/components/form/Button";
import type { CalendarSlotIdea } from "@/types/contentCalendar.type";
import { KitFieldMessage, KitIconButton, KitInput, KitTextAction, KitTextarea, kitLabelClass } from "../../components/kit";
import { PlatformPicker } from "../components/PlatformPicker";

/** Every field the editor writes — fits both an idea edit and a hand-written concept. */
export type ConceptDraftFields = Pick<CalendarSlotIdea, "title" | "synopsis" | "hooks" | "objectiveClaim" | "channels">;

const EMPTY_DRAFT: ConceptDraftFields = { title: "", synopsis: "", hooks: [], objectiveClaim: "", channels: [] };

const HOOKS_MAX = 5;
const HOOK_MAX = 300;

/**
 * A local draft stays intact on failed saves and polling refreshes. Sharing is separate.
 * Without `idea` it is the blank "write your own concept" form.
 */
export default function ConceptDraftEditor({
  idea = EMPTY_DRAFT,
  onSave,
  onCancel,
  saving,
  heading = "Edit this idea",
  hint = "Saving updates the team draft. The client keeps seeing the last version you sent.",
  submitLabel = "Save draft",
}: {
  idea?: ConceptDraftFields;
  onSave: (fields: ConceptDraftFields) => Promise<unknown>;
  onCancel: () => void;
  saving: boolean;
  heading?: string;
  hint?: string;
  submitLabel?: string;
}) {
  const [title, setTitle] = useState(idea.title);
  const [synopsis, setSynopsis] = useState(idea.synopsis);
  const [hooks, setHooks] = useState<string[]>(idea.hooks.length ? [...idea.hooks] : [""]);
  const [objective, setObjective] = useState(idea.objectiveClaim);
  const [channels, setChannels] = useState(idea.channels);
  const [error, setError] = useState<string | null>(null);
  const hookOptions = hooks.map((hook) => hook.trim()).filter(Boolean);
  const hooksInvalid = hookOptions.length === 0 || hookOptions.length > HOOKS_MAX || hookOptions.some((hook) => hook.length > HOOK_MAX);

  const setHookAt = (at: number, value: string) => setHooks((list) => list.map((hook, index) => (index === at ? value : hook)));
  const removeHookAt = (at: number) => setHooks((list) => (list.length > 1 ? list.filter((_, index) => index !== at) : [""]));

  return (
    <form
      className="mx-auto flex w-full max-w-[760px] flex-col gap-[18px]"
      onSubmit={async (event) => {
        event.preventDefault();
        if (saving) return;
        if (hooksInvalid) {
          setError("Keep one to five hook options, with no more than 300 characters each.");
          return;
        }
        setError(null);
        try {
          await onSave({
            title: title.trim(),
            synopsis: synopsis.trim(),
            hooks: hookOptions,
            objectiveClaim: objective.trim(),
            channels,
          });
          onCancel();
        } catch {
          setError("Your draft is still here. The save did not complete; try again before leaving.");
        }
      }}
    >
      <div className="flex flex-col gap-1.5">
        <h3 className="font-section text-[24px] font-bold leading-[29px] text-co-text">{heading}</h3>
        <p className="text-[14px] leading-[1.5] text-co-text2">{hint}</p>
      </div>
      <fieldset disabled={saving} className="flex min-w-0 flex-col gap-[18px]">
        <KitInput label="Idea title" required maxLength={160} value={title} onChange={(e) => setTitle(e.target.value)} dir="auto" />
        <KitTextarea label="The concept" required rows={5} maxLength={600} value={synopsis} onChange={(e) => setSynopsis(e.target.value)} dir="auto" />
        <div className="flex flex-col gap-2">
          <div className="flex items-center justify-between gap-3">
            <p className="text-[13px] font-semibold leading-[18px] text-co-text">Hooks</p>
            <p className="text-[12px] leading-4 text-co-muted">1–5 options · up to {HOOK_MAX} characters each</p>
          </div>
          {hooks.map((hook, index) => (
            <div key={index} className="flex items-center gap-2">
              <KitInput
                aria-label={`Hook ${index + 1}`}
                value={hook}
                maxLength={HOOK_MAX}
                dir="auto"
                wrapperClassName="min-w-0 flex-1"
                onChange={(event) => setHookAt(index, event.target.value)}
              />
              <KitIconButton
                icon={X}
                aria-label={`Remove hook ${index + 1}`}
                disabled={hooks.length === 1 && !hook}
                onClick={() => removeHookAt(index)}
              />
            </div>
          ))}
          {hooks.length < HOOKS_MAX ? (
            <KitTextAction icon={Plus} tone="accent" className="self-start" onClick={() => setHooks((list) => [...list, ""])}>
              Add a hook
            </KitTextAction>
          ) : null}
        </div>
        <KitTextarea label="Why this idea works" rows={3} maxLength={600} value={objective} onChange={(e) => setObjective(e.target.value)} dir="auto" />
        <div className="flex flex-col gap-2">
          <p className={kitLabelClass}>Channels</p>
          <PlatformPicker value={channels} onChange={setChannels} />
        </div>
      </fieldset>
      {error ? <KitFieldMessage>{error}</KitFieldMessage> : null}
      <div className="flex justify-end gap-2">
        <Button size="small" variant="secondary" buttonStyle="outline" isDisabled={saving} onPress={onCancel}>
          Cancel
        </Button>
        <Button type="submit" size="small" loading={saving} isDisabled={!title.trim() || !synopsis.trim() || hooksInvalid || saving}>
          {submitLabel}
        </Button>
      </div>
    </form>
  );
}
